import { useReactFlow } from '@xyflow/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { StructureCanvasHelper } from '../canvas';
import type { PlacedNode } from '../layout';

// Must be called inside <ReactFlowProvider>. Pans to an entity and marks it for a moment, so a chip leads the eye.
function useFocusEntity(nodes: ReadonlyArray<PlacedNode>, minZoom: number) {
  const rf = useReactFlow();
  const [focusedEntityId, setFocusedEntityId] = useState<number>();
  const pulseTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(pulseTimer.current), []);

  const focusEntity = useCallback(
    (entityId: number) => {
      const node = nodes.find((placed) => placed.entityId === entityId);
      if (!node) return;
      rf.setCenter(node.x + node.width / 2, node.y + node.height / 2, {
        zoom: Math.max(rf.getZoom(), minZoom),
        duration: StructureCanvasHelper.getMotionMs(
          StructureCanvasHelper.FOCUS_PAN_MS
        ),
      });
      setFocusedEntityId(entityId);
      clearTimeout(pulseTimer.current);
      pulseTimer.current = setTimeout(
        () => setFocusedEntityId(undefined),
        StructureCanvasHelper.FOCUS_PULSE_MS
      );
    },
    [rf, nodes, minZoom]
  );

  return { focusedEntityId, focusEntity };
}

export { useFocusEntity };
